import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/* =========================================================== */
/* ============ THE VISUAL ARCHIVE =========================== */
/* A small curated wall of cinematic frames from the world of
   CHERUBIM. Editorial staggered layout, hairline antique-gold
   frames, restrained hover, and a full-screen cinematic viewer
   (arrows / keyboard / swipe / counter). Artwork is never
   altered: thumbnails render at natural aspect (no crop) and
   the full-resolution file loads only when a frame is opened. */
/* =========================================================== */

const EASE = [0.16, 1, 0.3, 1];

const FRAMES = [
  { full: "/assets/art/archive-rooftop.jpg", thumb: "/assets/art/archive-rooftop-thumb.jpg",
    alt: "Cam watching the city from a rooftop at sunset",
    span: "md:col-span-7", shift: "" },
  { full: "/assets/art/hero-still-clean.jpg", thumb: "/assets/art/hero-still-clean-thumb.jpg",
    alt: "The winged Guardian standing watch over the city",
    span: "md:col-span-5", shift: "md:mt-24" },
  { full: "/assets/art/archive-heaven-van.jpg", thumb: "/assets/art/archive-heaven-van-thumb.jpg",
    alt: "The Heaven Ministries van outside the IronVale gates in the rain",
    span: "md:col-span-5", shift: "" },
  { full: "/assets/art/archive-workshop.jpg", thumb: "/assets/art/archive-workshop-thumb.jpg",
    alt: "Two builders studying a device at the Hope Center workbench",
    span: "md:col-span-7", shift: "md:mt-10" },
  { full: "/assets/art/archive-diner.jpg", thumb: "/assets/art/archive-diner-thumb.jpg",
    alt: "A late-night conversation at the Silver Diner",
    span: "md:col-span-4", shift: "" },
  { full: "/assets/art/archive-trinity-ui.jpg", thumb: "/assets/art/archive-trinity-ui-thumb.jpg",
    alt: "The TRINITY interface over a rain-lit windshield at night",
    span: "md:col-span-4", shift: "md:mt-16" },
  { full: "/assets/art/archive-alley.jpg", thumb: "/assets/art/archive-alley-thumb.jpg",
    alt: "The Guardian in a rain-dark alley, ready stance",
    span: "md:col-span-4", shift: "md:mt-6" },
  { full: "/assets/art/archive-confrontation.jpg", thumb: "/assets/art/archive-confrontation-thumb.jpg",
    alt: "A tense confrontation inside the Hope Center",
    span: "md:col-span-7", shift: "" },
  { full: "/assets/art/archive-shadrach-street.jpg", thumb: "/assets/art/archive-shadrach-street-thumb.jpg",
    alt: "Shadrach rolling through a night crowd in the rain",
    span: "md:col-span-5", shift: "md:mt-12" },
  { full: "/assets/art/archive-beale-ride.jpg", thumb: "/assets/art/archive-beale-ride-thumb.jpg",
    alt: "The Guardian riding through Beale Street at night",
    span: "md:col-span-6 md:col-start-4", shift: "md:mt-4" },
];

/* Subtle rise + fade as each frame enters the viewport */
const FrameReveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const VisualArchive = () => {
  const [index, setIndex] = useState(null);
  const touchX = useRef(null);

  const close = useCallback(() => setIndex(null), []);
  const go = useCallback((dir) => {
    setIndex((i) => (i === null ? i : (i + dir + FRAMES.length) % FRAMES.length));
  }, []);

  /* Keyboard arrows + ESC, scroll lock, and neighbor preloading
     while the cinematic viewer is open */
  useEffect(() => {
    if (index === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    [1, -1].forEach((d) => {
      const img = new Image();
      img.src = FRAMES[(index + d + FRAMES.length) % FRAMES.length].full;
    });
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      window.__lenis?.start();
    };
  }, [index, close, go]);

  return (
    <section id="visual-archive" data-testid="section-visual-archive"
             className="py-24 border-t border-[var(--cb-divider)]"
             style={{ background: "var(--cb-bg)" }}>
      <div className="max-w-[1100px] mx-auto px-6 md:px-8">
        <FrameReveal>
          <h2 className="font-headline text-[var(--cb-text)] text-[30px] md:text-[44px] uppercase tracking-[0.04em] text-center">
            The Visual Archive
          </h2>
          <div className="font-caps text-[10px] tracking-[0.3em] text-[var(--cb-gold)] text-center mt-4">
            A GLIMPSE INTO THE WORLD OF CHERUBIM
          </div>
          <div className="gold-rule" />
        </FrameReveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mt-14 md:mt-20 md:items-start">
          {FRAMES.map((f, i) => (
            <div key={f.full} className={`${f.span} ${f.shift}`}>
              <FrameReveal delay={(i % 3) * 0.08}>
                <button
                  type="button"
                  data-testid={`archive-frame-${i + 1}`}
                  aria-label={`View frame ${i + 1} full screen`}
                  onClick={() => setIndex(i)}
                  className="group block w-full text-left cursor-pointer transition-transform duration-[350ms] ease-out md:hover:-translate-y-[5px]"
                >
                  <div
                    className="border border-[rgba(197,160,89,0.35)] bg-[#0C0C0E] p-[6px] transition-shadow duration-[400ms] ease-out md:group-hover:border-[rgba(226,195,122,0.65)] md:group-hover:shadow-[0_24px_50px_-24px_rgba(0,0,0,0.9),0_0_34px_rgba(184,138,44,0.14),inset_0_1px_0_rgba(232,199,102,0.22)]"
                  >
                    <div className="overflow-hidden">
                      <img
                        src={f.thumb}
                        alt={f.alt}
                        loading="lazy"
                        decoding="async"
                        className="block w-full h-auto select-none transition-transform duration-[400ms] ease-out md:group-hover:scale-[1.015]"
                        draggable="false"
                      />
                    </div>
                  </div>
                </button>
              </FrameReveal>
            </div>
          ))}
        </div>
      </div>

      {/* ============ FULL-SCREEN CINEMATIC VIEWER ============ */}
      <AnimatePresence>
        {index !== null && (
          <motion.div
            data-testid="archive-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Visual Archive full-screen viewer"
            className="fixed inset-0 z-[160] w-screen h-[100dvh] flex items-center justify-center cursor-zoom-out"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28 }}
            style={{ background: "rgba(3,3,5,0.97)", overscrollBehavior: "contain" }}
            onClick={close}
            onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              if (touchX.current === null) return;
              const dx = e.changedTouches[0].clientX - touchX.current;
              touchX.current = null;
              if (Math.abs(dx) > 45) go(dx > 0 ? -1 : 1);
            }}
          >
            <button
              type="button"
              data-testid="archive-close"
              aria-label="Close viewer"
              onClick={close}
              className="fixed top-4 right-4 z-10 w-10 h-10 flex items-center justify-center text-[rgba(197,160,89,0.85)] hover:text-[#E2C37A] border border-[rgba(197,160,89,0.35)] hover:border-[rgba(226,195,122,0.65)] bg-black/40 transition-colors duration-300 cursor-pointer"
            >
              <X size={20} strokeWidth={1.6} />
            </button>

            <button
              type="button"
              data-testid="archive-prev"
              aria-label="Previous frame"
              onClick={(e) => { e.stopPropagation(); go(-1); }}
              className="fixed left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center text-[rgba(197,160,89,0.85)] hover:text-[#E2C37A] border border-[rgba(197,160,89,0.3)] hover:border-[rgba(226,195,122,0.6)] bg-black/40 transition-colors duration-300 cursor-pointer"
            >
              <ChevronLeft size={20} strokeWidth={1.6} />
            </button>
            <button
              type="button"
              data-testid="archive-next"
              aria-label="Next frame"
              onClick={(e) => { e.stopPropagation(); go(1); }}
              className="fixed right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center text-[rgba(197,160,89,0.85)] hover:text-[#E2C37A] border border-[rgba(197,160,89,0.3)] hover:border-[rgba(226,195,122,0.6)] bg-black/40 transition-colors duration-300 cursor-pointer"
            >
              <ChevronRight size={20} strokeWidth={1.6} />
            </button>

            <motion.img
              key={index}
              src={FRAMES[index].full}
              alt={FRAMES[index].alt}
              initial={{ opacity: 0, scale: 0.965 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.985 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="block object-contain select-none border border-[rgba(197,160,89,0.22)]"
              style={{ maxWidth: "94vw", maxHeight: "84dvh" }}
              draggable="false"
              onClick={(e) => e.stopPropagation()}
            />

            <div
              data-testid="archive-counter"
              className="fixed bottom-5 left-1/2 -translate-x-1/2 font-caps text-[10px] tracking-[0.3em] text-[rgba(197,160,89,0.7)]"
              onClick={(e) => e.stopPropagation()}
            >
              {String(index + 1).padStart(2, "0")} / {String(FRAMES.length).padStart(2, "0")}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default VisualArchive;
